import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { workAssignmentAPI, leaveAPI, holidayAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  FiCalendar,
  FiActivity,
  FiClock,
  FiShield,
  FiStar,
  FiTrendingUp,
  FiBarChart,
  FiUser,
  FiGift
} from './Icons';
import styled from 'styled-components';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    'en-US': enUS,
  },
});

const Container = styled.div`
  padding: ${props => props.theme.spacing?.lg || '24px'};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing?.md || '16px'};
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing?.xl || '32px'};
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.sm || '12px'};

  svg {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: ${props => props.theme.spacing?.lg || '24px'};
  }
`;

const InfoPanel = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius?.large || '12px'};
  padding: ${props => props.theme.spacing?.lg || '24px'};
  margin-bottom: ${props => props.theme.spacing?.lg || '24px'};
  width: 100%;
  box-sizing: border-box;
  box-shadow: ${props => props.theme.shadows.card};

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing?.md || '16px'};
    margin-bottom: ${props => props.theme.spacing?.md || '16px'};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${props => props.theme.spacing?.md || '16px'};
  margin-bottom: ${props => props.theme.spacing?.md || '16px'};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing?.sm || '12px'};
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius?.medium || '8px'};
  padding: ${props => props.theme.spacing?.md || '16px'};
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.medium};
    border-color: ${props => props.theme.colors.border.medium};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || props.theme.colors.primary};
  }
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${props => props.color || props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing?.sm || '12px'} auto;
  color: #ffffff;
  font-size: 16px;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
  margin-bottom: ${props => props.theme.spacing?.xs || '8px'};
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CalendarContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius?.large || '12px'};
  box-shadow: ${props => props.theme.shadows.card};
  padding: ${props => props.theme.spacing?.xl || '32px'};
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border.light};

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing?.lg || '24px'};
    border-radius: ${props => props.theme.borderRadius?.medium || '8px'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.theme.spacing?.md || '16px'};
  }

  /* React Big Calendar — brand-aligned overrides */
  .rbc-calendar {
    min-width: 800px;
    width: 100%;
    font-family: ${props => props.theme.fonts.main};
  }

  .rbc-month-view,
  .rbc-header,
  .rbc-header + .rbc-header,
  .rbc-day-bg + .rbc-day-bg,
  .rbc-month-row + .rbc-month-row {
    border-color: ${props => props.theme.colors.border.light};
  }

  .rbc-header {
    background: ${props => props.theme.colors.surfaceAlt};
    border-bottom: 1px solid ${props => props.theme.colors.border.light};
    padding: 10px 8px;
    font-weight: 600;
    color: ${props => props.theme.colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.72rem;
  }

  .rbc-today {
    background: ${props => props.theme.colors.primarySoft};
  }

  .rbc-off-range-bg {
    background: ${props => props.theme.colors.surfaceAlt};
  }

  .rbc-date-cell {
    padding: 4px;
    min-height: auto !important;
  }

  .rbc-date-cell > a {
    color: ${props => props.theme.colors.text.primary};
    font-weight: 600;
    text-decoration: none;
    font-variant-numeric: tabular-nums;
  }

  .rbc-date-cell > a:hover {
    color: ${props => props.theme.colors.primary};
  }

  /* Event styling */
  .rbc-event {
    border-radius: 6px;
    border: none !important;
    font-weight: 600;
    font-size: 0.7rem;
    padding: 2px 6px;
    margin: 1px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rbc-event:focus {
    outline: none;
  }

  .rbc-event-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rbc-row-segment {
    padding: 0 2px 1px 2px;
  }

  .rbc-day-slot .rbc-events-container {
    margin-right: 0 !important;
  }

  /* Auto-growing month grid: week rows size to their content so every
     event is visible inline (no "+N more"); the page scrolls instead of
     the grid clipping. Rows must be flex:none or RBC's equal-height flex
     layout squeezes them back into the container. */
  .rbc-month-view {
    height: auto;
    flex: none;
  }

  .rbc-month-row {
    flex: none;
    height: auto;
    min-height: 112px;
    overflow: visible;
  }

  .rbc-row-content {
    min-height: 100%;
  }

  /* RBC wraps row content in a scroll container when showAllEvents is on —
     unwrap it so content height drives the row height. */
  .rbc-row-content-scrollable {
    height: auto;
    overflow: visible;
  }

  .rbc-row-content-scrollable .rbc-row-content-scroll-container {
    height: auto;
    overflow: visible;
  }

  .rbc-toolbar {
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 8px;

    .rbc-btn-group {
      > button {
        background: ${props => props.theme.colors.surface};
        border: 1px solid ${props => props.theme.colors.border.medium};
        color: ${props => props.theme.colors.text.secondary};
        border-radius: ${props => props.theme.borderRadius?.medium || '8px'};
        margin: 0 2px;
        padding: 7px 14px;
        font-weight: 600;
        font-family: ${props => props.theme.fonts.main};
        transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

        &:hover {
          background: ${props => props.theme.colors.primarySoft};
          color: ${props => props.theme.colors.primary};
          border-color: ${props => props.theme.colors.primary};
        }

        &.rbc-active {
          background: ${props => props.theme.colors.primary};
          color: ${props => props.theme.colors.text.inverse};
          border-color: ${props => props.theme.colors.primary};
        }

        &.rbc-active:hover {
          background: ${props => props.theme.colors.primaryHover};
          color: ${props => props.theme.colors.text.inverse};
        }
      }
    }

    .rbc-toolbar-label {
      font-size: 1.25rem;
      font-weight: 700;
      color: ${props => props.theme.colors.text.primary};
      font-variant-numeric: tabular-nums;
    }
  }
`;

const Legend = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing?.sm || '12px'};
  margin-bottom: ${props => props.theme.spacing?.xl || '32px'};
  flex-wrap: wrap;
  align-items: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.sm || '8px'};
  padding: 5px 10px;
  background: ${props => props.theme.colors.surfaceAlt};
  border-radius: 999px;
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const LegendIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 10px;
`;

const LegendText = styled.span`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 500;
`;

const DetailSection = styled.div`
  margin-top: ${props => props.theme.spacing?.lg || '24px'};
  padding-top: ${props => props.theme.spacing?.lg || '24px'};
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const DetailTitle = styled.h4`
  margin: 0 0 ${props => props.theme.spacing?.md || '16px'} 0;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.sm || '12px'};

  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing?.md || '16px'};
`;

const DetailCard = styled.div`
  background: ${props => props.theme.colors.surfaceAlt};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius?.medium || '8px'};
  padding: ${props => props.theme.spacing?.md || '16px'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.border.medium};
  }
`;

const DetailLabel = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.xs || '8px'};
`;

const DetailValue = styled.span`
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const WorkCalendar = () => {
  const [assignments, setAssignments] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewByRole, setViewByRole] = useState(false);
  const [roleBasedAssignments, setRoleBasedAssignments] = useState([]);
  const { user } = useAuth();

  // Get related roles based on user's role
  const getRelatedRoles = (userRole) => {
    const roleMap = {
      'store_manager': ['store_manager', 'manager_assistant'],
      'manager_assistant': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'makeup_supervisor': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'photographer_supervisor': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor'],
      'retoucher_supervisor': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[userRole] || [userRole];
  };

  const fetchMyData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch my work assignments
      const assignmentsResponse = await workAssignmentAPI.getAll({ user_id: user.id });
      setAssignments(assignmentsResponse.data);

      // Fetch my leave requests
      const leaveResponse = await leaveAPI.getLeaveRequests({ user_id: user.id });
      setLeaveRequests(leaveResponse.data);

      // Fetch holidays for my holiday group
      if (user.holiday_group_id) {
        try {
          const holidaysResponse = await holidayAPI.getCurrentYear(user.holiday_group_id);
          setHolidays(holidaysResponse.data);
        } catch (holidayError) {
          console.error('Failed to fetch holidays:', holidayError);
          // Try fallback method
          try {
            const fallbackResponse = await holidayAPI.getAll({
              holiday_group_id: user.holiday_group_id
            });
            setHolidays(fallbackResponse.data);
          } catch (fallbackError) {
            console.error('Fallback holiday fetch failed:', fallbackError);
          }
        }
      }

    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleBasedAssignments = async () => {
    if (!user || !viewByRole) return;

    try {
      const relatedRoles = getRelatedRoles(user.role);

      // Scope to the visible month (±7 days for the grid's leading/trailing
      // days). The unscoped call returned only the newest 3000 rows, so team
      // events silently vanished when browsing older months.
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      start.setDate(start.getDate() - 7);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      end.setDate(end.getDate() + 7);

      const allAssignmentsResponse = await workAssignmentAPI.getAll({
        start_date: fmt(start),
        end_date: fmt(end),
      });

      // Filter assignments by related roles, excluding current user
      const filteredAssignments = allAssignmentsResponse.data.filter(assignment => {
        const assignmentRole = assignment.user?.role || assignment.role;
        return relatedRoles.includes(assignmentRole) && assignment.user_id !== user.id;
      });

      setRoleBasedAssignments(filteredAssignments);
    } catch (error) {
      console.error('Error fetching role-based assignments:', error);
    }
  };

  useEffect(() => {
    fetchMyData();
  }, [user]);

  useEffect(() => {
    if (viewByRole) {
      fetchRoleBasedAssignments();
    } else {
      setRoleBasedAssignments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewByRole, user, currentDate]);

  // Convert assignments to calendar events
  const workEvents = assignments.map(assignment => {
    const assignmentDate = new Date(assignment.assignment_date);
    const shiftType = assignment.shift_type.charAt(0).toUpperCase() + assignment.shift_type.slice(1);
    const branchName = assignment.branch?.name || `Branch ${assignment.branch_id}`;
    const shiftIcon = assignment.shift_type === 'day' ? '🌅' : '🌆';

    return {
      id: `work-${assignment.id}`,
      title: `${shiftIcon} ${shiftType} @ ${branchName}`,
      start: new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate(), 9, 0),
      end: new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate(), 17, 0),
      resource: { type: 'work', data: assignment, isMine: true },
    };
  });

  // Convert role-based assignments to calendar events (when viewByRole is enabled)
  const roleBasedEvents = viewByRole ? roleBasedAssignments.map(assignment => {
    const assignmentDate = new Date(assignment.assignment_date);
    const shiftType = assignment.shift_type.charAt(0).toUpperCase() + assignment.shift_type.slice(1);
    const branchName = assignment.branch?.name || `Branch ${assignment.branch_id}`;
    const userName = assignment.user?.name || 'Unknown';
    const shiftIcon = assignment.shift_type === 'day' ? '🌅' : '🌆';

    return {
      id: `role-work-${assignment.id}`,
      title: `${shiftIcon} ${userName} @ ${branchName}`,
      start: new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate(), 9, 0),
      end: new Date(assignmentDate.getFullYear(), assignmentDate.getMonth(), assignmentDate.getDate(), 17, 0),
      resource: { type: 'work', data: assignment, isMine: false },
    };
  }) : [];

  // Convert leave requests to calendar events
  const leaveEvents = leaveRequests
    .filter(leave => leave.status === 'approved')
    .map(leave => {
      const leaveDate = new Date(leave.leave_date);
      return {
        id: `leave-${leave.id}`,
        title: `🏖️ ${leave.leave_type?.name || 'Leave'}`,
        start: new Date(leaveDate.getFullYear(), leaveDate.getMonth(), leaveDate.getDate()),
        end: new Date(leaveDate.getFullYear(), leaveDate.getMonth(), leaveDate.getDate(), 23, 59),
        resource: { type: 'leave', data: leave },
        allDay: true,
      };
    });

  // Convert holidays to calendar events
  const holidayEvents = holidays.map(holiday => {
    const holidayDate = new Date(holiday.date);
    return {
      id: `holiday-${holiday.id}`,
      title: `🎉 ${holiday.name}`,
      start: new Date(holidayDate.getFullYear(), holidayDate.getMonth(), holidayDate.getDate()),
      end: new Date(holidayDate.getFullYear(), holidayDate.getMonth(), holidayDate.getDate(), 23, 59),
      resource: { type: 'holiday', data: holiday },
      allDay: true,
    };
  });

  const events = [...workEvents, ...roleBasedEvents, ...leaveEvents, ...holidayEvents];

  const eventStyleGetter = (event, start, end, isSelected) => {
    const { type, isMine } = event.resource;

    const baseStyle = {
      borderRadius: '6px',
      border: 'none',
      fontWeight: '600',
      fontSize: '0.7rem',
      padding: '2px 6px',
      margin: '1px 0',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      cursor: 'pointer'
    };

    if (type === 'work') {
      const assignment = event.resource.data;
      const isDayShift = assignment.shift_type === 'day';

      // Different styling for own assignments vs role-based assignments
      if (isMine) {
        return {
          style: {
            ...baseStyle,
            background: isDayShift ? '#2f6fb3' : '#c17a1a',
            color: '#ffffff',
          }
        };
      } else {
        // Role-based assignments (other employees) - lighter/translucent style
        return {
          style: {
            ...baseStyle,
            background: isDayShift ? '#8fb4dd' : '#e3c07a',
            color: '#2b2825',
            border: '1px dashed #6b645c',
            opacity: 0.9,
          }
        };
      }
    } else if (type === 'leave') {
      return {
        style: {
          ...baseStyle,
          background: '#2e7d5b',
          color: '#ffffff',
        }
      };
    } else if (type === 'holiday') {
      return {
        style: {
          ...baseStyle,
          background: '#930c00',
          color: '#ffffff',
        }
      };
    }

    return { style: baseStyle };
  };

  const getWorkStats = () => {
    const selectedMonth = currentDate.getMonth();
    const selectedYear = currentDate.getFullYear();

    const thisMonthAssignments = assignments.filter(a => {
      const assignmentDate = new Date(a.assignment_date);
      return assignmentDate.getMonth() === selectedMonth && assignmentDate.getFullYear() === selectedYear;
    });

    const thisMonthLeave = leaveRequests.filter(l => {
      const leaveDate = new Date(l.leave_date);
      return l.status === 'approved' &&
             leaveDate.getMonth() === selectedMonth &&
             leaveDate.getFullYear() === selectedYear;
    });

    const thisMonthHolidays = holidays.filter(h => {
      const holidayDate = new Date(h.date);
      return holidayDate.getMonth() === selectedMonth && holidayDate.getFullYear() === selectedYear;
    });

    // Group leave by type with dates
    const leaveByType = thisMonthLeave.reduce((acc, leave) => {
      const typeName = leave.leave_type?.name || 'Other Leave';
      if (!acc[typeName]) {
        acc[typeName] = [];
      }
      acc[typeName].push({
        date: leave.leave_date,
        reason: leave.reason || 'No reason provided'
      });
      return acc;
    }, {});

    // Count unique work days (not shifts)
    const uniqueWorkDates = new Set(thisMonthAssignments.map(a => a.assignment_date));

    return {
      workDays: uniqueWorkDates.size,
      leaveDays: thisMonthLeave.length,
      holidayDays: thisMonthHolidays.length,
      dayShifts: thisMonthAssignments.filter(a => a.shift_type === 'day').length,
      noonShifts: thisMonthAssignments.filter(a => a.shift_type === 'noon').length,
      leaveByType,
      leaveEntries: thisMonthLeave,
      holidays: thisMonthHolidays,
      monthName: currentDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })
    };
  };

  if (loading) {
    return (
      <Container>
        <Title>Work Schedule</Title>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b645c' }}>Loading...</div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Title>Work Schedule</Title>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b645c' }}>Please log in to view your schedule.</div>
      </Container>
    );
  }

  const stats = getWorkStats();

  return (
    <Container>
      <Title>
        <FiCalendar />
        {user.name}'s Work Schedule
      </Title>

      <CalendarContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <Legend style={{ flex: 1, marginBottom: 0 }}>
            <LegendItem>
              <LegendIcon color="#2f6fb3">
                <FiActivity />
              </LegendIcon>
              <LegendText>My Day Shift</LegendText>
            </LegendItem>
            <LegendItem>
              <LegendIcon color="#c17a1a">
                <FiClock />
              </LegendIcon>
              <LegendText>My Noon Shift</LegendText>
            </LegendItem>
            {viewByRole && (
              <>
                <LegendItem>
                  <LegendIcon color="#8fb4dd">
                    <FiUser />
                  </LegendIcon>
                  <LegendText>Team Day Shift</LegendText>
                </LegendItem>
                <LegendItem>
                  <LegendIcon color="#e3c07a">
                    <FiUser />
                  </LegendIcon>
                  <LegendText>Team Noon Shift</LegendText>
                </LegendItem>
              </>
            )}
            <LegendItem>
              <LegendIcon color="#2e7d5b">
                <FiShield />
              </LegendIcon>
              <LegendText>Approved Leave</LegendText>
            </LegendItem>
            <LegendItem>
              <LegendIcon color="#930c00">
                <FiStar />
              </LegendIcon>
              <LegendText>Public Holiday</LegendText>
            </LegendItem>
          </Legend>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#ffffff',
            padding: '9px 14px',
            borderRadius: '999px',
            border: '1px solid #e7e3db',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#930c00';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e7e3db';
          }}
          onClick={() => setViewByRole(!viewByRole)}
          >
            <input
              type="checkbox"
              id="viewByRole"
              checked={viewByRole}
              onChange={(e) => setViewByRole(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#930c00'
              }}
            />
            <label
              htmlFor="viewByRole"
              style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#6b645c',
                cursor: 'pointer',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FiTrendingUp style={{ fontSize: '1rem', color: '#930c00' }} />
              View by Role
            </label>
          </div>
        </div>

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          eventPropGetter={eventStyleGetter}
          views={['month']}
          defaultView="month"
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          style={{
            height: 'auto',
            minHeight: '560px'
          }}
          selectable={false}
          showMultiDayTimes={false}
          step={30}
          timeslots={2}
          doShowMoreDrillDown={false}
          showAllEvents={true}
        />
      </CalendarContainer>

      <InfoPanel>
        <h3 style={{
          marginTop: 0,
          marginBottom: '16px',
          color: '#2b2825',
          fontSize: '1.15rem',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FiUser style={{ color: '#930c00', fontSize: '1.125rem' }} />
          {user.name} - {stats.monthName} Overview
        </h3>

        <InfoGrid>
          <StatCard color="#2b2825">
            <StatIcon color="#2b2825">
              <FiCalendar />
            </StatIcon>
            <StatValue>{stats.workDays}</StatValue>
            <StatLabel>Total Work Days</StatLabel>
          </StatCard>

          <StatCard color="#2f6fb3">
            <StatIcon color="#2f6fb3">
              <FiActivity />
            </StatIcon>
            <StatValue>{stats.dayShifts}</StatValue>
            <StatLabel>Day Shifts</StatLabel>
          </StatCard>

          <StatCard color="#c17a1a">
            <StatIcon color="#c17a1a">
              <FiClock />
            </StatIcon>
            <StatValue>{stats.noonShifts}</StatValue>
            <StatLabel>Noon Shifts</StatLabel>
          </StatCard>

          <StatCard color="#2e7d5b">
            <StatIcon color="#2e7d5b">
              <FiShield />
            </StatIcon>
            <StatValue>{stats.leaveDays}</StatValue>
            <StatLabel>Leave Days</StatLabel>
          </StatCard>

          <StatCard color="#930c00">
            <StatIcon color="#930c00">
              <FiStar />
            </StatIcon>
            <StatValue>{stats.holidayDays}</StatValue>
            <StatLabel>Holiday Days</StatLabel>
          </StatCard>
        </InfoGrid>

        {/* Leave Type Breakdown */}
        {Object.keys(stats.leaveByType).length > 0 && (
          <DetailSection>
            <DetailTitle>
              <FiBarChart />
              Leave Breakdown by Type & Dates
            </DetailTitle>
            <DetailGrid>
              {Object.entries(stats.leaveByType).map(([leaveType, leaveDates]) => (
                <div key={leaveType} style={{ gridColumn: '1 / -1' }}>
                  <DetailCard style={{ marginBottom: '12px' }}>
                    <DetailLabel>
                      <FiShield style={{ color: '#2e7d5b' }} />
                      {leaveType} ({leaveDates.length} day{leaveDates.length !== 1 ? 's' : ''})
                    </DetailLabel>
                    <DetailValue>{leaveDates.length} total</DetailValue>
                  </DetailCard>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    {leaveDates.map((leave, index) => (
                      <div key={index} style={{
                        background: '#eef5f0',
                        border: '1px solid #cfe3d7',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontSize: '0.875rem'
                      }}>
                        <div style={{ fontWeight: '600', color: '#245c43' }}>
                          {new Date(leave.date).toLocaleDateString('en', {
                            month: 'short',
                            day: 'numeric',
                            weekday: 'short'
                          })}
                        </div>
                        {leave.reason && (
                          <div style={{ color: '#4a7862', fontSize: '0.75rem', marginTop: '2px' }}>
                            {leave.reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </DetailGrid>
          </DetailSection>
        )}

        {/* Holiday Breakdown */}
        {stats.holidays.length > 0 && (
          <DetailSection>
            <DetailTitle>
              <FiStar />
              Holiday Group Dates
            </DetailTitle>
            <DetailGrid>
              {stats.holidays.map((holiday, index) => (
                <DetailCard key={holiday.id}>
                  <DetailLabel>
                    <FiStar style={{ color: '#930c00' }} />
                    {holiday.name}
                  </DetailLabel>
                  <DetailValue>
                    {new Date(holiday.date).toLocaleDateString('en', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </DetailValue>
                </DetailCard>
              ))}
            </DetailGrid>
          </DetailSection>
        )}
      </InfoPanel>
    </Container>
  );
};

export default WorkCalendar;
